
import { useLocation } from 'react-router-dom';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';
import { useSupabaseAuth } from '@/hooks/auth';
import { useUserCourseAssignments } from '@/hooks/useUserCourseAssignments';
import { useSidebarPersistence } from '@/hooks/useSidebarPersistence';

export const useSidebarNavigation = () => {
  const location = useLocation();
  const { hasAccess } = useSupabaseAuth();
  const { assignments } = useUserCourseAssignments();
  const sidebarPersistence = useSidebarPersistence();
  
  const isActive = (path: string) => location.pathname === path;
  const isActivePath = (path: string) => location.pathname.startsWith(path);

  // Check if any course route is active for the "All Courses" parent
  const isCourseRouteActive = () => {
    return ALL_COURSE_CATEGORIES.some(category => 
      isActivePath(category.route) || isActive(category.route)
    );
  };

  // Filter visible course categories based on access and assignments
  const getVisibleCourseCategories = () => {
    if (hasAccess('admin')) {
      return ALL_COURSE_CATEGORIES;
    }

    // For non-admin users, show all categories but filter courses by assignments
    // If user has no assignments, show all categories with limited courses for discovery
    if (assignments.length === 0) {
      // Show first 3 courses from each category for discovery
      return ALL_COURSE_CATEGORIES.map(category => ({
        ...category,
        courses: category.courses.slice(0, 3)
      }));
    }

    // For users with assignments, only show categories with assigned courses
    return ALL_COURSE_CATEGORIES
      .map(category => {
        const assignedCourses = category.courses.filter(course =>
          assignments.some(assignment => assignment.course_id === course.id)
        );
        return {
          ...category,
          courses: assignedCourses
        };
      })
      .filter(category => category.courses.length > 0);
  };

  return {
    isActive,
    isActivePath,
    isCourseRouteActive,
    getVisibleCourseCategories,
    hasAdminAccess: hasAccess('admin'),
    ...sidebarPersistence
  };
};
